const timelineCards = [
  {
    number: "01",
    title: "Childhood",
    icon: "ri-seedling-line",
    description:
      "Living with chronic illness from an early age, learning to mask pain behind a smile and tie my worth to productivity.",
  },
  {
    number: "02",
    title: "The Cycle",
    icon: "ri-refresh-line",
    description:
      "Years of Boom and Bust: pushing past limits, crashing, recovering, and pushing again. My body kept sending the same message.",
  },
  {
    number: "03",
    title: "The Turning Point",
    icon: "ri-lightbulb-flash-line",
    description:
      "A profound realisation: rest is not retreat. Rest is a strategic reload. That truth changed everything.",
  },
  {
    number: "04",
    title: "Now",
    icon: "ri-heart-3-line",
    description:
      "Trained as an ICF Certified coach, I now guide others through the same transformation, living meaningfully alongside chronic illness.",
  },
];

export default function StorySection() {
  return (
    <section
      id="my-story"
      className="section-padding scroll-mt-24 bg-cover bg-center bg-no-repeat relative"
      style={{
        backgroundImage:
          "url('https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/dd09b505e9735ceb49b4ab0d41925161.png')",
      }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="font-semibold text-xs tracking-widest uppercase mb-3 font-sans text-accent-600">
            The Journey
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-800">
            About My Story
          </h2>
        </div>

        {/* Story Content */}
        <div className="grid md:grid-cols-2 gap-14 items-start mb-16">
          {/* Photo */}
          <div className="flex justify-center md:justify-end">
            <div className="relative">
              <div className="absolute -inset-3 rounded-full bg-accent-200/30 pointer-events-none" />
              <img
                alt="Federica — Energy Safe Transformational Coach"
                className="relative object-cover object-top w-72 h-72 rounded-full hover:scale-[1.04] hover:-translate-y-[4px] hover:shadow-lg transition-all duration-300 ease-out scale-x-[-1]"
                src="https://public.readdy.ai/ai/img_res/edited_24515a41aab68653180ae624c4fe6834_99c10a64.jpg"
              />
              <div className="mt-4 text-center">
                <div className="mx-auto mb-3 flex items-center justify-center gap-3">
                  <div className="h-px w-20 bg-primary-300" />
                  <div className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                  <div className="h-px w-20 bg-primary-300" />
                </div>
                <p className="font-heading text-lg font-semibold text-primary-800">
                  Federica Tedeschi
                </p>
                <p className="text-xs text-foreground-500 mt-1">
                  International Coaching Federation (ICF) Certified
                </p>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="space-y-5 text-foreground-600 text-sm leading-relaxed pt-2">
            <p className="text-base font-medium text-primary-700 font-heading italic">
              &ldquo;I spent years trying to outrun my own nervous system.&rdquo;
            </p>
            <p>
              For most of my life, my worth was tied to my productivity. I wore my &ldquo;busyness&rdquo; like armour, masking chronic pain with a smile and pushing until my body forced me to stop, over and over again. I thought if I just worked harder, I could beat the fatigue.
            </p>
            <p>
              I was wrong. Every crash taught me the same loud lesson:{" "}
              <strong className="text-primary-700">You cannot thrive while at war with your body.</strong>
            </p>
            <p>
              The turning point came when I stopped fighting and started listening. I realised that a meaningful life isn&apos;t something you find after you&apos;re cured: it&apos;s something you build alongside your illness.
            </p>
            <p>
              This realisation led me to become an{" "}
              <strong className="text-primary-700">ICF Certified Transformational Coach</strong>. I integrated my lived experience with{" "}
              <strong className="text-primary-700">Neuro-Linguistic Programming (NLP)</strong> to create the{" "}
              <strong>Energy-Safe Method</strong>. I built this framework because I walked this path first, and I know that while the journey is personal, you shouldn&apos;t have to walk it alone.
            </p>
            <div className="pt-4">
              <a
                href="https://calendly.com/federicatedeschi3/new-meeting"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary-500 text-background-50 px-7 py-3 rounded-lg font-medium text-sm hover:bg-primary-600 hover:scale-[1.05] hover:-translate-y-[3px] hover:shadow-lg transition-all duration-300 ease-out whitespace-nowrap cursor-pointer inline-block"
              >
                Book a Free Conversation
              </a>
            </div>
          </div>
        </div>

        {/* Timeline Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {timelineCards.map((card) => (
            <div
              key={card.number}
              className="bg-background-100 rounded-xl p-6 flex flex-col gap-3 hover:shadow-card hover:scale-[1.03] hover:-translate-y-[3px] transition-all duration-300 ease-out"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-200/40">
                <i className={`${card.icon} text-lg text-accent-700`} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-accent-700 tracking-widest uppercase">
                  {card.number}
                </span>
                <span className="font-heading text-base font-semibold text-primary-800">
                  {card.title}
                </span>
              </div>
              <p className="text-foreground-600 text-sm leading-relaxed">
                {card.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}